import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { elementScroll, useVirtualizer } from '@tanstack/react-virtual';
import { attachReaderKeyboardScroll } from './useReaderKeyboardScroll';

const ESTIMATED_HEIGHT = 640;
const ANCHOR_ELEMENTS = 'h1,h2,h3,h4,h5,h6,p,li,tr,pre,img';

// Remember a visible block, rather than only the article's top: an image can
// finish loading above that block inside the same article.
export function captureReaderAnchor(container) {
    if (!container) return null;
    const bounds = container.getBoundingClientRect();
    const breadcrumb = container.querySelector('nav[aria-label="breadcrumb"]');
    const line = Math.max(bounds.top, breadcrumb?.parentElement.getBoundingClientRect().bottom || bounds.top);
    for (const section of container.querySelectorAll('.article-section')) {
        const rect = section.getBoundingClientRect();
        if (rect.bottom <= line || rect.top >= bounds.bottom) continue;
        const blocks = Array.from(section.querySelectorAll(ANCHOR_ELEMENTS));
        const block = blocks.findIndex(element => {
            const r = element.getBoundingClientRect();
            return r.height > 0 && r.bottom > line && r.top < bounds.bottom;
        });
        const element = block < 0 ? section : blocks[block];
        const viewportTop = element.getBoundingClientRect().top;
        return { index: Number(section.dataset.index), block, top: viewportTop - bounds.top, viewportTop };
    }
    return null;
}

export function restoreReaderAnchor(container, anchor) {
    if (!container || !anchor) return false;
    const section = container.querySelector(`.article-section[data-index="${anchor.index}"]`);
    if (!section) return false;
    const element = section.querySelectorAll(ANCHOR_ELEMENTS)[anchor.block] || section;
    const target = anchor.viewportTop ?? (container.getBoundingClientRect().top + anchor.top);
    const delta = element.getBoundingClientRect().top - target;
    if (Math.abs(delta) > 0.5) container.scrollTop += delta;
    return true;
}

export default function useArticleWindow({ articles, flatToc, containerRef, contentRef, scrollAdjustmentRef }) {
    const containerElement = containerRef.current;
    const contentElement = contentRef.current;
    const indices = useMemo(() => Object.keys(articles).map(Number).sort((a, b) => a - b), [articles]);
    const indicesRef = useRef(indices);
    indicesRef.current = indices;
    const virtualizerRef = useRef(null);
    const anchorRef = useRef(null);
    const resizeAnchorRef = useRef(null);
    const pendingAnchorRef = useRef(null);
    const structuralAnchorRef = useRef(null);
    const userScrollPendingRef = useRef(false);
    const sizesRef = useRef(new Map());
    const widthRef = useRef(0);
    const previousIndices = useRef([]);
    const [scrollMargin, setScrollMargin] = useState(0);
    const [, refreshAnchor] = useState(0);
    const restoreAnchor = useCallback((anchor, cacheChanged = true) => {
        // A resize can change the range after the DOM has already reflowed.
        // Keep its pre-reflow target instead of replacing it with a later snapshot.
        if (!cacheChanged && (pendingAnchorRef.current || resizeAnchorRef.current)) return;
        pendingAnchorRef.current = anchor;
        anchorRef.current = anchor;
        structuralAnchorRef.current = anchor;
    }, []);

    const removed = previousIndices.current.some(index => !indices.includes(index));
    const retainedAnchor = removed && indices.includes(anchorRef.current?.index);
    // Pin the reader before calculating the shorter range, so eviction cannot
    // temporarily replace the visible article with the end of the list.
    if (retainedAnchor && !pendingAnchorRef.current) pendingAnchorRef.current = anchorRef.current;

    const getItemKey = useCallback(index => flatToc[indices[index]]?._id ?? indices[index], [indices, flatToc]);
    const rangeExtractor = useCallback(range => {
        const instance = virtualizerRef.current;
        const measurements = instance?.measurementsCache;
        const height = instance?.scrollRect?.height || 900;
        const pending = pendingAnchorRef.current;
        const pinnedIndex = pending ? indicesRef.current.indexOf(pending.index) : -1;
        let start = pinnedIndex < 0 ? range.startIndex : pinnedIndex;
        let end = pinnedIndex < 0 ? range.endIndex : pinnedIndex;
        const offset = pinnedIndex < 0 ? (instance?.scrollOffset || 0) : measurements?.[pinnedIndex]?.start || 0;
        while (start > 0 && measurements?.[start - 1]?.end > offset - height * 2) start--;
        while (end < range.count - 1 && measurements?.[end + 1]?.start < offset + height * 3) end++;
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, []);

    const virtualizer = useVirtualizer({
        count: indices.length,
        getScrollElement: () => containerRef.current,
        getItemKey,
        estimateSize: index => {
            const size = sizesRef.current.get(getItemKey(index));
            // Old-width measurements are estimates only, until remeasured.
            return size ? Math.max(80, size.height * size.width / (widthRef.current || size.width)) : ESTIMATED_HEIGHT;
        },
        indexAttribute: 'data-virtual-index',
        scrollMargin,
        rangeExtractor,
        measureElement: (element, entry, instance) => {
            const height = element.getBoundingClientRect().height;
            const key = instance.options.getItemKey(Number(element.dataset.virtualIndex));
            const previous = sizesRef.current.get(key);
            if (entry && previous && Math.abs(previous.height - height) > 0.5 && !pendingAnchorRef.current) {
                pendingAnchorRef.current = resizeAnchorRef.current || anchorRef.current;
            }
            sizesRef.current.set(key, { height, width: contentRef.current?.clientWidth || 1 });
            return height;
        },
        scrollToFn: (offset, options, instance) => {
            const before = instance.scrollElement?.scrollTop;
            // Even writing the same offset cancels Chromium's native keyboard
            // scroll animation. Leave it running when no correction is needed.
            if (options.behavior !== 'smooth' && offset + (options.adjustments || 0) === before) return;
            elementScroll(offset, options, instance);
            const after = instance.scrollElement?.scrollTop;
            // The virtualizer also writes the current offset as a no-op. Marking
            // that as a correction hides the user's native scroll event.
            if (after !== before) scrollAdjustmentRef.current = after;
        },
    });
    virtualizerRef.current = virtualizer;
    virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, delta, instance) =>
        !resizeAnchorRef.current && !pendingAnchorRef.current && item.end <= (instance.scrollOffset || 0);

    useLayoutEffect(() => {
        const container = containerElement;
        const content = contentElement;
        if (!container || !content) return undefined;
        let resizeTimer;
        const finishResize = () => {
            if (!resizeAnchorRef.current) return;
            window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                pendingAnchorRef.current = resizeAnchorRef.current;
                resizeAnchorRef.current = null;
                refreshAnchor(value => value + 1);
            }, 150);
        };
        const onScroll = () => {
            const adjustment = scrollAdjustmentRef.current;
            const userScroll = userScrollPendingRef.current;
            userScrollPendingRef.current = false;
            if (!userScroll && adjustment != null && Math.abs(adjustment - container.scrollTop) < 1) return;
            structuralAnchorRef.current = null;
            if (userScroll) pendingAnchorRef.current = null;
            if (!resizeAnchorRef.current) anchorRef.current = captureReaderAnchor(container);
        };
        const onResizeStart = event => {
            if (event.target instanceof Element && window.getComputedStyle(event.target).cursor === 'col-resize') {
                resizeAnchorRef.current = captureReaderAnchor(container);
            } else {
                structuralAnchorRef.current = null;
            }
        };
        const onUserScroll = event => {
            if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
            if (event.type === 'wheel' && event.deltaY === 0) return;
            if (event.type !== 'touchstart') userScrollPendingRef.current = true;
            window.clearTimeout(resizeTimer);
            structuralAnchorRef.current = null;
            anchorRef.current = null;
            resizeAnchorRef.current = null;
            pendingAnchorRef.current = null;
            virtualizer.cancelScroll();
        };
        const observer = new ResizeObserver(() => {
            const width = content.clientWidth;
            if (width > 0 && Math.abs(width - widthRef.current) > 0.5) {
                resizeAnchorRef.current ||= anchorRef.current;
                pendingAnchorRef.current = resizeAnchorRef.current;
                widthRef.current = width;
                virtualizer.measure();
                finishResize();
            }
            setScrollMargin(current => {
                const next = content.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
                return Math.abs(next - current) > 0.5 ? next : current;
            });
        });
        observer.observe(container);
        observer.observe(content);
        // Input must win even if a virtualizer correction coalesces with its
        // native scroll event. Capture it before the virtualizer handles it.
        container.addEventListener('scroll', onScroll, { passive: true, capture: true });
        container.addEventListener('wheel', onUserScroll, { passive: true });
        container.addEventListener('touchstart', onUserScroll, { passive: true });
        container.addEventListener('touchmove', onUserScroll, { passive: true });
        container.addEventListener('keydown', onUserScroll);
        const detachKeyboard = attachReaderKeyboardScroll(container, scrollAdjustmentRef, () => {
            userScrollPendingRef.current = true;
            onScroll();
        });
        document.addEventListener('pointerdown', onResizeStart, true);
        document.addEventListener('pointerup', finishResize);
        return () => {
            detachKeyboard();
            observer.disconnect();
            window.clearTimeout(resizeTimer);
            container.removeEventListener('scroll', onScroll, true);
            container.removeEventListener('wheel', onUserScroll);
            container.removeEventListener('touchstart', onUserScroll);
            container.removeEventListener('touchmove', onUserScroll);
            container.removeEventListener('keydown', onUserScroll);
            document.removeEventListener('pointerdown', onResizeStart, true);
            document.removeEventListener('pointerup', finishResize);
        };
    // These refs belong to ancestor DOM nodes. They can still be null during
    // this child's first layout effect; attach when the nodes become available.
    }, [containerElement, contentElement, scrollAdjustmentRef, virtualizer]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        // ArticleSection prepares the DOM before measuring it. Prune detached
        // measurement refs after the commit so cached sizes do not retain DOM.
        virtualizer.measureElement(null);
        const prepended = previousIndices.current.length && indices[0] < previousIndices.current[0];
        previousIndices.current = indices;
        if (removed && !indices.includes(anchorRef.current?.index)) {
            resizeAnchorRef.current = null;
            pendingAnchorRef.current = null;
            anchorRef.current = null;
            structuralAnchorRef.current = null;
            sizesRef.current.clear();
            virtualizer.measure();
            virtualizer.scrollToOffset(0, { behavior: 'auto' });
        } else if (removed) {
            const keys = new Set(indices.map(index => flatToc[index]?._id ?? index));
            for (const key of sizesRef.current.keys()) {
                if (!keys.has(key)) sizesRef.current.delete(key);
            }
            // Stable article keys let retained measurements survive eviction.
            // Resetting them all makes short articles estimates again and
            // introduces a second spacer/scroll correction on every article.
            for (const key of virtualizer.itemSizeCache.keys()) {
                if (!keys.has(key)) virtualizer.itemSizeCache.delete(key);
            }
        } else if (prepended && !pendingAnchorRef.current) {
            pendingAnchorRef.current = anchorRef.current;
        }
        const beforeRestore = container?.scrollTop;
        if (restoreReaderAnchor(container, pendingAnchorRef.current || resizeAnchorRef.current)) {
            virtualizer.cancelScroll();
            if (container.scrollTop !== beforeRestore) scrollAdjustmentRef.current = container.scrollTop;
            pendingAnchorRef.current = null;
        }
        // A prepend can reveal the tail of an earlier article. Keep the original
        // block while its images settle, until the reader actually scrolls again.
        anchorRef.current = resizeAnchorRef.current || structuralAnchorRef.current || captureReaderAnchor(container);
    });

    const items = virtualizer.getVirtualItems();
    return {
        indices,
        items,
        measureElement: virtualizer.measureElement,
        restoreAnchor,
        topSpace: items.length ? Math.max(0, items[0].start - scrollMargin) : 0,
        bottomSpace: items.length ? Math.max(0, virtualizer.getTotalSize() - (items[items.length - 1].end - scrollMargin)) : 0,
    };
}
